import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, CircularProgress, Stack } from "@mui/material";
import emailjs from "@emailjs/browser";
import { CustomTextField } from "@shared/components/CustomTextField";
import { contactSchema } from "@shared/utils/yupSchema";
import { useNotification } from "@shared/contexts/NotificationContext";

// EmailJS credentials. These are non-secret by design — the public
// key is meant to ship to the browser, and the service/template IDs
// are identifiers, not access tokens. Kept as constants here (rather
// than VITE_-prefixed env vars) because the contact form has a single
// destination; if we ever need per-environment routing or rotation
// without a code edit, move these to env.
const EMAILJS_SERVICE_ID = "service_qa4dngd";
const EMAILJS_TEMPLATE_ID = "template_k8erqmi";
const EMAILJS_PUBLIC_KEY = "YCgJN0RiQuvCapejL";

interface ContactData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface Props {
  /**
   * Called after a successful send. The modal uses this to close
   * itself — the form deliberately doesn't know about modal state.
   */
  onSent?: () => void;
}

export default function ContactForm({ onSent }: Props) {
  const { showSuccess, showError } = useNotification();
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactData>({
    resolver: yupResolver(contactSchema),
    defaultValues: { name: "", email: "", subject: "", message: "" },
  });

  // Guard the async send handler from updating state after unmount —
  // the modal can close (or the user can navigate) while the network
  // call is still in flight.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const submit = async (data: ContactData) => {
    setSubmitting(true);
    try {
      // @emailjs/browser's send() types templateParams as
      // Record<string, unknown>; ContactData is a concrete subtype but
      // the structural mismatch needs an assertion. Safe because every
      // field is a string by virtue of the Yup schema.
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        data as unknown as Record<string, unknown>,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      if (!mountedRef.current) return;
      showSuccess("Message sent — we'll get back to you shortly.");
      reset();
      onSent?.();
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      const message =
        (err as { text?: string; message?: string })?.text ||
        (err as { message?: string })?.message ||
        "Could not send message. Please try again.";
      showError(message);
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(submit)}
      noValidate
      spacing={2}
      sx={{ pt: 1 }}
    >
      <CustomTextField
        {...register("name")}
        autoFocus
        autoComplete="name"
        name="name"
        label="Name"
        disabled={submitting}
        error={!!errors.name}
        helperText={errors.name?.message ?? " "}
        fullWidth
      />
      <CustomTextField
        {...register("email")}
        autoComplete="email"
        name="email"
        label="Email"
        type="email"
        disabled={submitting}
        error={!!errors.email}
        helperText={errors.email?.message ?? " "}
        fullWidth
      />
      <CustomTextField
        {...register("subject")}
        name="subject"
        label="Subject"
        disabled={submitting}
        error={!!errors.subject}
        helperText={errors.subject?.message ?? " "}
        fullWidth
      />
      <CustomTextField
        {...register("message")}
        name="message"
        label="Message"
        placeholder="How can we help?"
        disabled={submitting}
        multiline
        minRows={4}
        error={!!errors.message}
        helperText={errors.message?.message ?? " "}
        fullWidth
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={submitting}
        startIcon={
          submitting ? <CircularProgress size={18} color="inherit" /> : null
        }
      >
        {submitting ? "Sending…" : "Send message"}
      </Button>
    </Stack>
  );
}
